import { ThemeEvents } from '@theme/events';

/**
 * Clears cart.note once the cart becomes empty.
 *
 * Shopify does NOT clear the note when the last line is removed — only when an
 * order completes. Before this, a buyer who put "PO 44821-B" in the cart, emptied
 * it without ordering, and came back later found that PO still attached to a
 * completely unrelated cart. A stale purchase order is worse than none: it goes
 * out on the NDS purchase order (NdsXmlBuilder puts order.note on the PO XML) and
 * lands on the wrong invoice in AP.
 *
 * Lives in the header, next to <cart-icon>, because that is the only component
 * present on every page that already sees cart updates — the note field itself
 * only renders while the cart has items, so it cannot observe its own emptying.
 *
 * Initial state comes from Liquid so a page that loads with an already-empty cart
 * and a leftover note is cleaned up too, without spending a /cart.js fetch on
 * every page view.
 */
class CartNoteReset extends HTMLElement {
  /** Guards against re-clearing (and against a clear → cart:update → clear loop). */
  #cleared = false;

  connectedCallback() {
    this.#cleared = false;

    if (this.dataset.cartEmpty === 'true' && this.dataset.hasNote === 'true') {
      this.#clear();
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const data = event?.detail?.data || {};
    // A failed add says nothing about whether the cart is empty — it may still hold everything.
    if (data.didError) return;
    const itemCount = data.itemCount;
    if (typeof itemCount !== 'number') return;

    // Product-form adds (and surfaces that mimic them, e.g. the parts diagram) report the units just
    // ADDED — cart-icon adds them to the badge — never the cart total, so 0 can't mean "emptied".
    if (data.source === 'product-form-component') {
      if (itemCount > 0) this.#cleared = false;
      return;
    }

    // Cart refilled — re-arm, so a note left behind by a LATER emptying is caught.
    if (itemCount > 0) {
      this.#cleared = false;
      return;
    }

    this.#clear();
  };

  async #clear() {
    if (this.#cleared) return;
    this.#cleared = true;

    try {
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: '' }),
      });
    } catch (_) {
      // Fail silently and leave the note alone. A stale note is a nuisance;
      // a thrown error in the header on every cart change is worse.
      this.#cleared = false;
    }
  }
}

if (!customElements.get('cart-note-reset')) {
  customElements.define('cart-note-reset', CartNoteReset);
}
